import { useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { toZonedTime, format } from 'date-fns-tz'
import { DownloadSimple } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import ModalContainer from '../../../../components/ModalContainer/ModelContainer'
import Icon from '../../../../components/Icons'
import TextCell from '../components/TextCell/TextCell'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import {
  updateChecklist,
  toggleRevisionMode,
} from '../../../../reducers/Organisation/checklistsReducer'
import { getAssetLink } from '../../../../helpers/get-asset-link'
import API from '../../../../api/api'
import { ZoomFrame } from '@/components/ui/zoomable-image'

import { getProperComponent } from './helpers'
import ConfirmRevisionModal from './ConfirmRevisionModal'

const ChecklistsTaskDetailsModal = ({
  data,
  resident,
  headerTitle,
  timezone,
  onClose = () => {},
  updateChecklist,
  toggleRevisionMode,
}) => {
  const { t } = useTranslation(['checklists-tasks'])
  const [showRevisionConfirm, setRevisionConfirm] = useState(false)
  const [isSubmitting, setSubmitting] = useState(false)
  const { displaySnackbar } = useSnackbar()

  const onRequestRevision = async (revisionRequestNote) => {
    setSubmitting(true)
    try {
      await updateChecklist({ ...data, status: 'Revision Requested', revisionRequestNote })
      displaySnackbar(t('checklists-tasks:taskDetailsModal.revisionSuccess'))
      setRevisionConfirm(false)
      toggleRevisionMode(true)
      onClose()
    } catch (e) {
      displaySnackbar(t('checklists-tasks:taskDetailsModal.revisionError'), true)
      throw new Error(e)
    } finally {
      setSubmitting(false)
    }
  }

  const onApprove = async () => {
    setSubmitting(true)
    try {
      await updateChecklist({ ...data, status: 'Completed' })
      displaySnackbar(t('checklists-tasks:taskDetailsModal.approveSuccess'))
      onClose()
    } catch (e) {
      displaySnackbar(t('checklists-tasks:taskDetailsModal.approveError'), true)
      throw new Error(e)
    } finally {
      setSubmitting(false)
    }
  }

  const downloadSignature = () => {
    API.getChecklistSignature(data.id).then(async (response) => {
      const rawBlob = response.data
      const url = URL.createObjectURL(rawBlob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = 'signature.png'
      document.body.appendChild(anchor)
      anchor.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(anchor)
    })
  }

  const handleViewPDFClick = () => {
    const file = data.attachment
      ? `${import.meta.env.VITE_ASSETS_URL}/chitown-images/checklist-attachments/${data.attachment}`
      : ''

    window.open(file, '_blank')
  }

  return (
    <ModalContainer
      additionalClass={'checklist-modal'}
      onClose={onClose}
      headerTitle={headerTitle}
      headerIcon={<Icon icon="checkClipboard" />}
    >
      <div className="checklist-modal-content checklists-tasks-details">
        {resident && (
          <div className="checklists-tasks-details-head">
            <div>
              <ZoomFrame
                className="zoom-avatar-frame"
                hintSize="2rem"
                src={getAssetLink({ path: 'chitown-avatar/avatar', file: resident.userAvatar })}
                title={resident.userFullName}
              >
                <img
                  src={getAssetLink({ path: 'chitown-avatar/avatar', file: resident.userAvatar })}
                />
              </ZoomFrame>
              <TextCell title={resident.userFullName} text={resident.userEmail} />
            </div>
            {data.completedAt && (
              <div>
                <TextCell
                  title={t('checklists-tasks:taskDetailsModal.completedOnLabel')}
                  text={format(toZonedTime(data.completedAt, timezone), 'MM/dd/yyyy', {
                    timeZone: timezone,
                  })}
                />
              </div>
            )}
          </div>
        )}
        <div className="checklist-name">{data.name}</div>
        {data.attachment ? (
          <div>
            <h2>{t('checklists-tasks:taskDetailsModal.viewAdditionalDetails')}</h2>
            <button
              type="submit"
              className="button button-bordered"
              disabled={!data.attachment}
              onClick={handleViewPDFClick}
            >
              {t('checklists-tasks:taskDetailsModal.viewPdfButton')}
            </button>
          </div>
        ) : null}
        {data.instructions && (
          <div
            className="checklists-tasks-details-instructions ql-editor"
            dangerouslySetInnerHTML={{ __html: data.instructions }}
          />
        )}
        {data.inputs
          ? data.inputs.map((inputData) => getProperComponent(inputData, null, t))
          : null}
        {data.signatures && (
          <div className="signature-component">
            <div className="file-row" onClick={downloadSignature}>
              {t('checklists-tasks:taskDetailsModal.signatureLabel')} <DownloadSimple size={20} />
            </div>
          </div>
        )}
        {data.status === 'Pending Approval' && (
          <div className="checklist-details-footer">
            <button
              type="submit"
              className="button button-bordered"
              onClick={() => setRevisionConfirm(true)}
              disabled={isSubmitting}
            >
              {t('checklists-tasks:taskDetailsModal.requestRevisionButton')}
            </button>
            <button
              type="submit"
              className="button button-primary"
              onClick={onApprove}
              disabled={isSubmitting}
            >
              {t('checklists-tasks:taskDetailsModal.approveButton')}
            </button>
          </div>
        )}
      </div>
      <ConfirmRevisionModal
        body={t('checklists-tasks:taskDetailsModal.requestRevisionConfirmBody', {
          taskName: data.name,
          residentName: resident
            ? resident.userFullName
            : t('checklists-tasks:shared.residentFallback'),
        })}
        isOpen={showRevisionConfirm}
        isLoading={isSubmitting}
        onCancel={() => setRevisionConfirm(false)}
        onConfirm={(note) => onRequestRevision(note)}
      />
    </ModalContainer>
  )
}

ChecklistsTaskDetailsModal.propTypes = {
  headerTitle: PropTypes.string,
  data: PropTypes.object,
  resident: PropTypes.object,
  timezone: PropTypes.string,
  onClose: PropTypes.func,
  updateChecklist: PropTypes.func,
  toggleRevisionMode: PropTypes.func,
}

export default connect(null, {
  updateChecklist,
  toggleRevisionMode,
})(ChecklistsTaskDetailsModal)
